import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, LogOut, ExternalLink, Trash2, Plus } from "lucide-react";
import { getContent, updateContent } from "@/lib/api";
import { buildContent } from "@/lib/content";
import { useAuth } from "@/lib/auth";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { VideoUploader } from "@/components/admin/VideoUploader";
import { AdminJobs } from "@/components/admin/AdminJobs";
import { AdminApplications } from "@/components/admin/AdminApplications";
import { AdminCatering } from "@/components/admin/AdminCatering";

const TABS = ["Allgemein", "Startseite", "Aktion", "Über uns", "Olivenöl", "Kontakt", "Stellen", "Bewerbungen", "Catering", "Gutschein", "SEO", "Rechtliches"];

const LABELS = {
  heroKicker: "Hero – Kicker", heroTitle1: "Hero – Zeile 1", heroTitle2: "Hero – Zeile 2",
  heroAccent: "Hero – Akzent", heroText: "Hero – Text",
  aboutTitle: "Über-Block – Titel", aboutP1: "Über-Block – Absatz 1", aboutP2: "Über-Block – Absatz 2",
  oliveTitle: "Olivenöl – Titel", oliveText: "Olivenöl – Text", olivePrice: "Olivenöl – Preis",
  takeawayTitle: "Mitnehmen – Titel", takeawayText: "Mitnehmen – Text",
  ctaTitle: "CTA – Titel", ctaText: "CTA – Text",
  heroTitle: "Hero – Titel",
  introTitle: "Intro – Titel", introP1: "Intro – Absatz 1", introP2: "Intro – Absatz 2",
  teamTitle: "Team – Titel", teamText: "Team – Text",
  locationTitle: "Standort – Titel", locationText: "Standort – Text",
  kicker: "Kicker", title: "Titel", p1: "Absatz 1", p2: "Absatz 2", p3: "Absatz 3",
  price: "Preis", quote: "Zitat", location: "Herkunft", intro: "Einleitung",
};

const SEO_PAGES = [
  { key: "home", label: "Startseite" },
  { key: "about", label: "Über uns" },
  { key: "olive", label: "Olivenöl" },
  { key: "contact", label: "Kontakt" },
  { key: "career", label: "Karriere" },
  { key: "catering", label: "Catering & Events" },
  { key: "gutschein", label: "Gutschein" },
];

const TextField = ({ label, value, onChange, multiline }) => (
  <div>
    <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1 block">{label}</label>
    {multiline ? (
      <textarea data-testid={`field-${label}`} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none resize-y" />
    ) : (
      <input data-testid={`field-${label}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none" />
    )}
  </div>
);

const MULTILINE_KEYS = new Set([
  "heroText", "aboutP1", "aboutP2", "oliveText", "takeawayText", "ctaText",
  "introP1", "introP2", "teamText", "locationText", "p1", "p2", "p3",
  "intro", "desc", "quote", "location", "text", "description", "impressum", "datenschutz",
]);

const Section = ({ title, children }) => (
  <div className="mb-8"><h3 className="font-serif text-2xl text-wine mb-4">{title}</h3>{children}</div>
);

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

const SeasonDatePicker = ({ testid, label, value, onDay, onMonth, onYear }) => {
  const [yyyy, mm, dd] = (value || "2026-01-01").split("-");
  const daysInMonth = new Date(parseInt(yyyy, 10), parseInt(mm, 10), 0).getDate();
  const nowYear = new Date().getFullYear();
  const years = [];
  for (let y = nowYear - 1; y <= nowYear + 6; y++) years.push(String(y));
  if (!years.includes(yyyy)) years.push(yyyy);
  return (
    <div>
      <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-2 block">{label}</label>
      <div className="flex gap-2">
        <select data-testid={`${testid}-day`} value={dd} onChange={(e) => onDay(e.target.value)}
          className="w-20 bg-white border border-wine/15 rounded px-2 py-2 text-sm focus:border-wine focus:outline-none">
          {Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => (
            <option key={d} value={d}>{parseInt(d, 10)}.</option>
          ))}
        </select>
        <select data-testid={`${testid}-month`} value={mm} onChange={(e) => onMonth(e.target.value)}
          className="flex-1 bg-white border border-wine/15 rounded px-2 py-2 text-sm focus:border-wine focus:outline-none">
          {MONTHS.map((name, i) => {
            const m = String(i + 1).padStart(2, "0");
            return <option key={m} value={m}>{name}</option>;
          })}
        </select>
        <select data-testid={`${testid}-year`} value={yyyy} onChange={(e) => onYear(e.target.value)}
          className="w-24 bg-white border border-wine/15 rounded px-2 py-2 text-sm focus:border-wine focus:outline-none">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
  );
};


export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("Allgemein");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getContent().then((stored) => setC(buildContent(stored || {})));
  }, []);

  const setText = (page, key, val) =>
    setC((p) => ({ ...p, text: { ...p.text, [page]: { ...p.text[page], [key]: val } } }));
  const setImg = (key, val) => setC((p) => ({ ...p, images: { ...p.images, [key]: val } }));
  const setBrand = (key, val) => setC((p) => ({ ...p, brand: { ...p.brand, [key]: val } }));
  const setBanner = (key, val) => setC((p) => ({ ...p, banner: { ...p.banner, [key]: val } }));
  const setHour = (i, key, val) =>
    setC((p) => ({ ...p, hours: p.hours.map((h, idx) => (idx === i ? { ...h, [key]: val } : h)) }));
  const setHourArr = (arrKey, i, key, val) =>
    setC((p) => ({ ...p, [arrKey]: (p[arrKey] || []).map((h, idx) => (idx === i ? { ...h, [key]: val } : h)) }));
  const setSeason = (val) => setC((p) => ({ ...p, season: val }));
  const setSeasonPart = (key, part, val) =>
    setC((p) => {
      const def = key === "seasonSummerStart" ? "2026-04-01" : "2026-11-01";
      let [yyyy, mm, dd] = (p[key] || def).split("-");
      if (part === "year") yyyy = val;
      if (part === "month") mm = val;
      if (part === "day") dd = val;
      const maxDay = new Date(parseInt(yyyy, 10), parseInt(mm, 10), 0).getDate();
      if (parseInt(dd, 10) > maxDay) dd = String(maxDay).padStart(2, "0");
      return { ...p, [key]: `${yyyy}-${mm}-${dd}` };
    });
  const addGallery = () => setC((p) => ({ ...p, gallery: [...(p.gallery || []), ""] }));
  const setGalleryItem = (i, val) => setC((p) => ({ ...p, gallery: p.gallery.map((g, idx) => (idx === i ? val : g)) }));
  const removeGallery = (i) => setC((p) => ({ ...p, gallery: p.gallery.filter((_, idx) => idx !== i) }));
  const setSpecial = (key, val) => setC((p) => ({ ...p, special: { ...p.special, [key]: val } }));
  const setLive = (key, val) => setC((p) => ({ ...p, liveCam: { ...p.liveCam, [key]: val } }));
  const setTageskarte = (val) => setC((p) => ({ ...p, tageskarte: { ...p.tageskarte, enabled: val } }));
  const addTeam = () => setC((p) => ({ ...p, team: [...(p.team || []), { name: "", role: "", image: "" }] }));
  const setTeamItem = (i, key, val) => setC((p) => ({ ...p, team: p.team.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)) }));
  const removeTeam = (i) => setC((p) => ({ ...p, team: p.team.filter((_, idx) => idx !== i) }));
  const addHoliday = () => setC((p) => ({ ...p, holidays: [...(p.holidays || []), { date: "", label: "", time: "", closed: true }] }));
  const setHolidayItem = (i, key, val) => setC((p) => ({ ...p, holidays: p.holidays.map((h, idx) => (idx === i ? { ...h, [key]: val } : h)) }));
  const removeHoliday = (i) => setC((p) => ({ ...p, holidays: p.holidays.filter((_, idx) => idx !== i) }));
  const setSeo = (page, key, val) =>
    setC((p) => ({ ...p, seo: { ...p.seo, [page]: { ...p.seo[page], [key]: val } } }));
  const setLegal = (key, val) =>
    setC((p) => ({ ...p, text: { ...p.text, legal: { ...p.text.legal, [key]: val } } }));

  const save = async () => {
    setSaving(true);
    try {
      const cleanGallery = (c.gallery || []).filter((g) => g && g.trim());
      const cleanTeam = (c.team || []).filter((m) => m.name || m.image);
      const cleanHolidays = (c.holidays || []).filter((h) => h.date);
      await updateContent({ brand: c.brand, images: c.images, hours: c.hours, hoursSummer: c.hoursSummer, season: c.season, seasonSummerStart: c.seasonSummerStart, seasonWinterStart: c.seasonWinterStart, gallery: cleanGallery, banner: c.banner, special: c.special, team: cleanTeam, holidays: cleanHolidays, liveCam: c.liveCam, tageskarte: c.tageskarte, streetView: c.streetView, text: c.text, seo: c.seo });
      toast.success("Änderungen gespeichert & veröffentlicht.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="animate-spin text-wine" /></div>;
  }

  const renderTextGroup = (page, keys) => (
    <div className="space-y-5">
      {keys.map((k) => (
        <TextField key={k} label={LABELS[k] || k} value={c.text[page][k]}
          onChange={(v) => setText(page, k, v)} multiline={MULTILINE_KEYS.has(k)} />
      ))}
    </div>
  );

  const img = (k, label) => (
    <ImageUploader label={label} value={c.images[k]} onChange={(v) => setImg(k, v)} />
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand">
      {/* top bar */}
      <header className="bg-ink text-cream sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-serif text-2xl">La Contadina · Editor</p>
            <p className="text-xs text-cream/60">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-xs uppercase tracking-[0.15em] text-cream/70 hover:text-gold flex items-center gap-1"><ExternalLink size={14} /> Website</a>
            <button data-testid="admin-save-btn" onClick={save} disabled={saving}
              className="btn-pop bg-gold text-ink px-5 py-2.5 text-xs uppercase tracking-[0.15em] flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
            </button>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/admin/login"); }}
              className="text-cream/70 hover:text-gold"><LogOut size={18} /></button>
          </div>
        </div>
        <div className="border-t border-cream/10">
          <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                className={`px-4 py-3 text-xs uppercase tracking-[0.15em] whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-gold text-gold" : "border-transparent text-cream/60 hover:text-cream"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {tab === "Allgemein" && (
          <>
            <Section title="Kontaktdaten">
              <div className="space-y-5">
                <TextField label="Telefon" value={c.brand.phone} onChange={(v) => setBrand("phone", v)} />
                <TextField label="Adresse" value={c.brand.address} onChange={(v) => setBrand("address", v)} />
                <TextField label="Facebook-Link" value={c.brand.facebook} onChange={(v) => setBrand("facebook", v)} />
                <TextField label="Zahlungshinweis" value={c.brand.payment} onChange={(v) => setBrand("payment", v)} />
                <TextField label="Küchen-Hinweis" value={c.brand.hoursNote} onChange={(v) => setBrand("hoursNote", v)} />
              </div>
            </Section>
            <Section title="Öffnungszeiten">
              <div className="mb-5">
                <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-2 block">Aktive Saison</label>
                <select data-testid="season-select" value={c.season || "auto"} onChange={(e) => setSeason(e.target.value)}
                  className="w-full bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none">
                  <option value="auto">Automatisch nach Kalender (Datum wählbar)</option>
                  <option value="winter">Immer Winterzeit anzeigen</option>
                  <option value="sommer">Immer Sommerzeit anzeigen</option>
                </select>
                {(c.season || "auto") === "auto" && (
                  <div className="mt-4 grid sm:grid-cols-2 gap-4 bg-white/60 border border-wine/10 rounded p-4">
                    <p className="sm:col-span-2 text-xs text-ink-soft">Lege fest, ab welchem Datum automatisch die Sommer- bzw. Winterzeiten angezeigt werden. Die Umstellung wiederholt sich jedes Jahr am gleichen Tag.</p>
                    <SeasonDatePicker testid="season-summer-start" label="Sommerzeit beginnt am"
                      value={c.seasonSummerStart || "2026-04-01"}
                      onDay={(v) => setSeasonPart("seasonSummerStart", "day", v)}
                      onMonth={(v) => setSeasonPart("seasonSummerStart", "month", v)}
                      onYear={(v) => setSeasonPart("seasonSummerStart", "year", v)} />
                    <SeasonDatePicker testid="season-winter-start" label="Winterzeit beginnt am"
                      value={c.seasonWinterStart || "2026-11-01"}
                      onDay={(v) => setSeasonPart("seasonWinterStart", "day", v)}
                      onMonth={(v) => setSeasonPart("seasonWinterStart", "month", v)}
                      onYear={(v) => setSeasonPart("seasonWinterStart", "year", v)} />
                  </div>
                )}
              </div>

              <p className="font-serif text-xl text-ink mb-3">Winterzeit</p>
              <div className="space-y-3 mb-8">
                {(c.hours || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-28 text-sm text-ink-soft shrink-0">{h.day}</span>
                    <input value={h.time || ""} onChange={(e) => setHour(i, "time", e.target.value)}
                      className="flex-1 bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none" />
                    <label className="flex items-center gap-1 text-xs text-ink-muted">
                      <input type="checkbox" checked={!!h.closed} onChange={(e) => setHour(i, "closed", e.target.checked)} /> Ruhetag
                    </label>
                  </div>
                ))}
              </div>

              <p className="font-serif text-xl text-ink mb-3">Sommerzeit</p>
              <div className="space-y-3">
                {(c.hoursSummer || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-28 text-sm text-ink-soft shrink-0">{h.day}</span>
                    <input value={h.time || ""} onChange={(e) => setHourArr("hoursSummer", i, "time", e.target.value)}
                      className="flex-1 bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none" />
                    <label className="flex items-center gap-1 text-xs text-ink-muted">
                      <input type="checkbox" checked={!!h.closed} onChange={(e) => setHourArr("hoursSummer", i, "closed", e.target.checked)} /> Ruhetag
                    </label>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Sonderöffnungszeiten / Feiertage">
              <p className="text-sm text-ink-soft mb-5">Einzelne Ausnahmetage (z. B. Feiertage). An diesen Tagen überschreiben sie die regulären Zeiten und werden auf der Kontaktseite angezeigt.</p>
              <div className="space-y-4">
                {(c.holidays || []).map((h, i) => (
                  <div key={i} className="border border-gold/20 p-3 bg-white/50 space-y-3" data-testid={`holiday-edit-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-[0.12em] text-ink-muted">Eintrag {i + 1}</span>
                      <button onClick={() => removeHoliday(i)} className="text-wine hover:text-wine-dark" data-testid={`holiday-remove-${i}`}><Trash2 size={16} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1 block">Datum</label>
                        <input type="date" value={h.date || ""} onChange={(e) => setHolidayItem(i, "date", e.target.value)} className="w-full bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none" />
                      </div>
                      <TextField label="Anlass" value={h.label} onChange={(v) => setHolidayItem(i, "label", v)} />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-ink-muted">
                      <input type="checkbox" checked={!!h.closed} onChange={(e) => setHolidayItem(i, "closed", e.target.checked)} /> Geschlossen (Ruhetag)
                    </label>
                    {!h.closed && (
                      <TextField label="Öffnungszeit (z. B. 12:00 – 18:00)" value={h.time} onChange={(v) => setHolidayItem(i, "time", v)} />
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addHoliday} data-testid="holiday-add-btn"
                className="mt-4 inline-flex items-center gap-2 border border-wine text-wine hover:bg-wine hover:text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors">
                <Plus size={16} /> Sondertag hinzufügen
              </button>
            </Section>

            <Section title="Hinweis-Banner (z. B. Betriebsferien)">
              <label className="flex items-center gap-2 mb-4 text-sm">
                <input data-testid="banner-toggle" type="checkbox" checked={!!c.banner.enabled} onChange={(e) => setBanner("enabled", e.target.checked)} />
                Banner auf der Startseite anzeigen
              </label>
              <div className="space-y-5">
                <TextField label="Banner-Titel" value={c.banner.title} onChange={(v) => setBanner("title", v)} />
                <TextField label="Banner-Text" value={c.banner.text} onChange={(v) => setBanner("text", v)} multiline />
                <div className="grid sm:grid-cols-2 gap-4 bg-white/60 border border-wine/10 rounded p-4">
                  <p className="sm:col-span-2 text-xs text-ink-soft">Optional: Zeitraum festlegen, in dem der Banner automatisch erscheint und danach wieder verschwindet. Lässt du die Felder leer, wird der Banner angezeigt, solange der Schalter oben aktiv ist.</p>
                  <div>
                    <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1.5 block">Anzeigen ab (Von)</label>
                    <input data-testid="banner-start" type="date" value={c.banner.startDate || ""} onChange={(e) => setBanner("startDate", e.target.value)}
                      className="w-full bg-white border border-wine/15 rounded px-3 py-2.5 text-sm focus:border-wine focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1.5 block">Anzeigen bis (Bis)</label>
                    <input data-testid="banner-end" type="date" value={c.banner.endDate || ""} onChange={(e) => setBanner("endDate", e.target.value)}
                      className="w-full bg-white border border-wine/15 rounded px-3 py-2.5 text-sm focus:border-wine focus:outline-none" />
                  </div>
                </div>
              </div>
            </Section>
          </>
        )}

        {tab === "Startseite" && (
          <>
            <Section title="Texte">{renderTextGroup("home", Object.keys(c.text.home))}</Section>
            <Section title="Tageskarte / Sonderkarte">
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="tageskarte-toggle" type="checkbox" checked={!!c.tageskarte?.enabled} onChange={(e) => setTageskarte(e.target.checked)} />
                Tageskarte-Bereich auf der Startseite anzeigen
              </label>
              <p className="text-sm text-ink-soft mt-3">Blendet den Hinweisbereich „Unsere Tageskarte" mit dem Button zur externen Tageskarte ein oder aus.</p>
            </Section>
            <Section title="Hero-Video">
              <p className="text-sm text-ink-soft mb-4">Hintergrundvideo im Kopfbereich der Startseite. Wird automatisch stummgeschaltet & in Schleife abgespielt.</p>
              <VideoUploader label="hero-video" value={c.images.heroVideo} onChange={(v) => setImg("heroVideo", v)} />
            </Section>
            <Section title="Live aus der Küche">
              <label className="flex items-center gap-2 mb-4 text-sm">
                <input data-testid="live-toggle" type="checkbox" checked={!!c.liveCam?.enabled} onChange={(e) => setLive("enabled", e.target.checked)} />
                Live-Bild auf der Startseite anzeigen
              </label>
              <p className="text-sm text-ink-soft mb-4">Der Bereich erscheint nur, wenn ein Gerät gerade sendet. Kein Ton, keine Aufzeichnung – es wird stets nur das aktuelle Bild gezeigt.</p>
              <div className="space-y-5 mb-5">
                <TextField label="Titel" value={c.liveCam?.title} onChange={(v) => setLive("title", v)} />
                <TextField label="Text" value={c.liveCam?.text} onChange={(v) => setLive("text", v)} multiline />
              </div>
              <a href="/live-sender" target="_blank" rel="noreferrer" data-testid="open-live-sender"
                className="inline-flex items-center gap-2 border border-wine text-wine hover:bg-wine hover:text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors">
                <ExternalLink size={16} /> Sender-Seite öffnen (auf dem Küchen-Gerät)
              </a>
              <p className="text-xs text-ink-muted mt-3">Tipp: Öffne diese Seite auf dem Handy/Tablet am Ofen und tippe auf „Live starten".</p>
            </Section>
            <Section title="Bilder">
              <div className="space-y-6">
                {img("hero", "Hero-Hintergrund (Bild-Fallback)")}
                {img("aboutWine", "Über-Block Bild")}
                {img("oliveBottle", "Olivenöl Bild")}
                {img("gallery1", "CTA-Hintergrund")}
              </div>
            </Section>
          </>
        )}

        {tab === "Über uns" && (
          <>
            <Section title="Texte">{renderTextGroup("about", Object.keys(c.text.about))}</Section>
            <Section title="Bilder">
              <div className="space-y-6">
                {img("aboutPottery", "Kopfbild")}
                {img("terrace", "Intro-Bild (Terrasse)")}
              </div>
            </Section>
            <Section title={'Foto-Album · „Momente bei La Contadina"'}>
              <p className="text-sm text-ink-soft mb-5">Lade beliebig viele eigene Fotos hoch. Das erste Foto wird groß dargestellt. Reihenfolge = Anzeigereihenfolge.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {(c.gallery || []).map((g, i) => (
                  <div key={i} className="border border-gold/20 p-3 bg-white/50" data-testid={`gallery-edit-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-[0.12em] text-ink-muted">Foto {i + 1}{i === 0 && " · groß"}</span>
                      <button onClick={() => removeGallery(i)} className="text-wine hover:text-wine-dark" data-testid={`gallery-remove-${i}`}><Trash2 size={16} /></button>
                    </div>
                    <ImageUploader label={`galerie-${i}`} value={g} onChange={(v) => setGalleryItem(i, v)} />
                  </div>
                ))}
              </div>
              <button onClick={addGallery} data-testid="gallery-add-btn"
                className="mt-5 inline-flex items-center gap-2 border border-wine text-wine hover:bg-wine hover:text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors">
                <Plus size={16} /> Foto hinzufügen
              </button>
            </Section>
            <Section title="Team-Vorstellung">
              <p className="text-sm text-ink-soft mb-5">Stelle dein Team vor (Name, Rolle, Foto). Wird auf der Seite „Über uns" angezeigt. Ohne Einträge bleibt der Bereich ausgeblendet.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {(c.team || []).map((m, i) => (
                  <div key={i} className="border border-gold/20 p-3 bg-white/50 space-y-3" data-testid={`team-edit-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-[0.12em] text-ink-muted">Mitglied {i + 1}</span>
                      <button onClick={() => removeTeam(i)} className="text-wine hover:text-wine-dark" data-testid={`team-remove-${i}`}><Trash2 size={16} /></button>
                    </div>
                    <TextField label="Name" value={m.name} onChange={(v) => setTeamItem(i, "name", v)} />
                    <TextField label="Rolle" value={m.role} onChange={(v) => setTeamItem(i, "role", v)} />
                    <ImageUploader label={`team-${i}`} value={m.image} onChange={(v) => setTeamItem(i, "image", v)} />
                  </div>
                ))}
              </div>
              <button onClick={addTeam} data-testid="team-add-btn"
                className="mt-5 inline-flex items-center gap-2 border border-wine text-wine hover:bg-wine hover:text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors">
                <Plus size={16} /> Mitglied hinzufügen
              </button>
            </Section>
          </>
        )}

        {tab === "Olivenöl" && (
          <>
            <Section title="Texte">{renderTextGroup("olive", Object.keys(c.text.olive))}</Section>
            <Section title="Bilder">
              <div className="space-y-6">
                {img("oliveHarvest", "Kopfbild (Ernte)")}
                {img("oliveBottle", "Flaschenbild")}
              </div>
            </Section>
          </>
        )}

        {tab === "Kontakt" && (
          <>
            <Section title="Texte">{renderTextGroup("contact", Object.keys(c.text.contact))}</Section>
            <Section title="Kopfbild (oben auf der Seite)">
              <p className="text-sm text-ink-soft mb-4">Stimmungsvolles Bild im Kopfbereich der Kontakt-Seite. Leer lassen, um stattdessen den hellen Hintergrund zu zeigen.</p>
              {img("contactHero", "Kontakt Kopfbild")}
            </Section>
            <Section title="360° Street View">
              <p className="text-sm text-ink-soft mb-3">Einbettungs-URL der 360°-Ansicht (Google Maps → Street View → Teilen → „Karte einbetten" → die <code>src</code>-URL einfügen). Leer lassen, um den Rundgang auszublenden.</p>
              <TextField label="Street-View Einbettungs-URL" value={c.streetView} onChange={(v) => setC((p) => ({ ...p, streetView: v }))} multiline />
            </Section>
          </>
        )}

        {tab === "Stellen" && (
          <>
            <Section title="Seiteninhalt – Karriere">
              {renderTextGroup("career", ["heroTitle", "heroText"])}
              <div className="mt-5">{img("careerHero", "Hero-Bild (optional)")}</div>
            </Section>
            <AdminJobs />
          </>
        )}

        {tab === "Bewerbungen" && <AdminApplications />}

        {tab === "Catering" && (
          <>
            <Section title="Seiteninhalt – Catering">
              {renderTextGroup("catering", ["heroTitle", "heroText"])}
              <div className="mt-5">{img("cateringHero", "Hero-Bild (optional)")}</div>
            </Section>
            <AdminCatering />
          </>
        )}

        {tab === "Gutschein" && (
          <Section title="Seiteninhalt – Gutschein">
            {renderTextGroup("gutschein", ["heroTitle", "heroText"])}
            <div className="mt-5">{img("gutscheinHero", "Hero-Bild (optional)")}</div>
          </Section>
        )}

        {tab === "Aktion" && (
          <Section title="Saison-Spezial / Aktions-Banner">
            <label className="flex items-center gap-2 mb-5 text-sm">
              <input data-testid="special-toggle" type="checkbox" checked={!!c.special.enabled} onChange={(e) => setSpecial("enabled", e.target.checked)} />
              Aktion auf der Startseite anzeigen
            </label>
            <div className="space-y-5">
              <TextField label="Kicker (klein, oben)" value={c.special.kicker} onChange={(v) => setSpecial("kicker", v)} />
              <TextField label="Titel" value={c.special.title} onChange={(v) => setSpecial("title", v)} />
              <TextField label="Text" value={c.special.text} onChange={(v) => setSpecial("text", v)} multiline />
              <div className="grid grid-cols-2 gap-5">
                <TextField label="Button-Text (optional)" value={c.special.ctaLabel} onChange={(v) => setSpecial("ctaLabel", v)} />
                <TextField label="Button-Link (optional)" value={c.special.ctaUrl} onChange={(v) => setSpecial("ctaUrl", v)} />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1 block">Von (optional)</label>
                  <input data-testid="special-start" type="date" value={c.special.startDate || ""} onChange={(e) => setSpecial("startDate", e.target.value)} className="w-full bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1 block">Bis (optional)</label>
                  <input data-testid="special-end" type="date" value={c.special.endDate || ""} onChange={(e) => setSpecial("endDate", e.target.value)} className="w-full bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs tracking-[0.12em] uppercase text-ink-muted mb-1 block">Hintergrundbild (optional)</label>
                <ImageUploader label="special-img" value={c.special.image} onChange={(v) => setSpecial("image", v)} />
              </div>
              <p className="text-xs text-ink-muted">Tipp: Mit „Von/Bis" wird die Aktion nur im gewählten Zeitraum angezeigt – ideal für „Trüffelwochen" oder „Spargelzeit".</p>
            </div>
          </Section>
        )}

        {tab === "SEO" && (
          <>
            <p className="text-sm text-ink-soft mb-6">Diese Angaben erscheinen in Google-Suchergebnissen und beim Teilen auf Social Media. Titel ca. 50–60 Zeichen, Beschreibung ca. 150–160 Zeichen.</p>
            {SEO_PAGES.map((pg) => (
              <Section key={pg.key} title={pg.label}>
                <div className="space-y-5">
                  <TextField label="Meta-Titel" value={c.seo[pg.key]?.title} onChange={(v) => setSeo(pg.key, "title", v)} />
                  <TextField label="Meta-Beschreibung" value={c.seo[pg.key]?.description} onChange={(v) => setSeo(pg.key, "description", v)} multiline />
                </div>
              </Section>
            ))}
          </>
        )}

        {tab === "Rechtliches" && (
          <>
            <Section title="Impressum">
              <TextField label="Impressum-Text" value={c.text.legal?.impressum} onChange={(v) => setLegal("impressum", v)} multiline />
            </Section>
            <Section title="Datenschutzerklärung">
              <TextField label="Datenschutz-Text" value={c.text.legal?.datenschutz} onChange={(v) => setLegal("datenschutz", v)} multiline />
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
